import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, CircleAlert, Calendar, FileText, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const TYPE_META = {
  follow_up: { icon: Users, label: "Follow-up overdue" },
  proposal_expiry: { icon: FileText, label: "Proposal expiring" },
  upcoming_workshop: { icon: Calendar, label: "Upcoming workshop" },
  schedule_meeting: { icon: CircleAlert, label: "Schedule meeting" },
};

const SEVERITY = {
  warning: { dot: "#D4A547", label: "bg-[#D4A547]/15 text-[#A6741F]" },
  danger: { dot: "#A23E3E", label: "bg-[#A23E3E]/15 text-[#A23E3E]" },
  info: { dot: "#5D3A1F", label: "bg-[#5D3A1F]/15 text-[#5D3A1F]" },
};

const NotificationsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/notifications")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = items.reduce((acc, it) => {
    acc[it.type] = acc[it.type] || [];
    acc[it.type].push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Stay on top</div>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Notification Center</h1>
        <p className="text-[#6B5944] mt-1.5">Urgent actions, follow-ups, and upcoming engagements.</p>
      </div>

      {loading ? <div className="text-center py-16 text-[#6B5944]">Loading…</div> : items.length === 0 ? (
        <div className="ccs-card p-12 text-center">
          <Bell className="w-10 h-10 text-[#C28A2E] mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display text-xl font-medium text-[#2A1810]">All caught up</div>
          <p className="text-[#6B5944] text-sm mt-1">No urgent actions right now — beautiful work, Joylance.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([type, list]) => {
            const meta = TYPE_META[type] || { icon: Bell, label: type };
            return (
              <section key={type} data-testid={`notif-group-${type}`}>
                <div className="flex items-center gap-2 mb-3">
                  <meta.icon className="w-4 h-4 text-[#C28A2E]" strokeWidth={1.7} />
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#2A1810]">{meta.label}</div>
                  <Badge variant="secondary" className="bg-[#EBE3D2] text-[#6B5944] text-[10px]">{list.length}</Badge>
                </div>
                <div className="space-y-2.5">
                  {list.map((it) => {
                    const sev = SEVERITY[it.severity] || SEVERITY.info;
                    return (
                      <div key={it.id} className="ccs-card ccs-card-hover p-4 flex items-start gap-4" data-testid={`notif-${it.id}`}>
                        <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: sev.dot }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-[#2A1810]">{it.title}</div>
                          <div className="text-xs text-[#6B5944] mt-0.5">{it.description}</div>
                        </div>
                        <Badge className={`${sev.label} rounded-full text-[10px] font-medium`}>{it.severity}</Badge>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
